import { createSlice } from '@reduxjs/toolkit'
import { signOut } from 'store/auth'
import { FirestoreDatabase } from 'index'
import { Collection } from 'constants/index'
import { toast } from 'utils/notify'
import firebase from 'firebase'
import { chunk, first } from 'lodash'

export const initialState = {
  error: null,
  inProgress: false,
  badgeConfiguration: null,
  badgeConfigurations: [],
  badgeSubject: null,
  badgesSubjectsData: [],
  selectedSubjectsRows: [],
  subjectsCurrentPage: 0,
  subjectsPageSize: 10,
  currentPage: 0,
  pageSize: 10
}

const badgesSlice = createSlice({
  name: 'badges',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    selectedSubjectsRows: (state, action) => {
      state.selectedSubjectsRows = action.payload
    },
    setBadgeSubject: (state, action) => {
      state.badgeSubject = action.payload
    },
    setBadgeConfiguration: (state, action) => {
      state.badgeConfiguration = action.payload
    },
    setBadgeConfigurations: (state, action) => {
      state.badgeConfigurations = action.payload
    },
    setBadgesSubjectsData: (state, action) => {
      state.badgesSubjectsData = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setSubjectsCurrentPage: (state, action) => {
      state.subjectsCurrentPage = action.payload
    },
    setSubjectsPageSize: (state, action) => {
      state.subjectsPageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.badgeConfiguration = null
      state.badgeConfigurations = []
      state.badgesSubjectsData = []
      state.selectedSubjectsRows = []
      state.badgeSubject = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

const peopleType = Object.freeze({
  ATTENDEE: 'Attendee',
  EXHIBITOR: 'Exhibitor',
  SPEAKER: 'Speaker',
  SPONSOR: 'Sponsor',
  MODERATOR: 'Moderator'
})

export const fetchPeopleBasedOnType = async (type, conferenceId) => {
  try {
    const people = []
    if (type === peopleType.SPEAKER || type === peopleType.MODERATOR) {
      const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
        .where('conferenceId', '==', conferenceId)
        .where('roles', 'array-contains', type === peopleType.SPEAKER ? 'presenter' : 'moderator')
        .get()

      snapshot.forEach((doc) => {
        people.push({
          id: doc.id,
          ...doc.data(),
          organization: doc.data().company,
          role: type === peopleType.SPEAKER ? peopleType.SPEAKER : peopleType.MODERATOR,
          type: type === peopleType.SPEAKER ? peopleType.SPEAKER : peopleType.MODERATOR
        })
      })
      people.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
    } else if (type === peopleType.EXHIBITOR || type === peopleType.SPONSOR) {
      const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
        .where('conferenceId', '==', conferenceId)
        .where('sponsorshipTier', '==', 1)
        .limit(1)
        .get()
      const exhibitorLevelId = snapshot?.docs?.length ? snapshot.docs[0].id : null

      const orgsSnapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
        .where('conferenceId', '==', conferenceId)
        .where('sponsorshipLevelId', type === peopleType.EXHIBITOR ? '==' : '!=', exhibitorLevelId)
        .get()

      const organizationIds = orgsSnapshot.docs.map((doc) => doc.id)

      if (organizationIds.length > 0) {
        const orgIdChunks = chunk(organizationIds, 10)
        // Process each chunk
        for (const chunk of orgIdChunks) {
          const orgAttendees = await FirestoreDatabase.collection(Collection.ATTENDEES)
            .where('conferenceId', '==', conferenceId)
            .where('organizationId', 'in', chunk)
            .get()

          orgAttendees.forEach((doc) => {
            people.push({
              id: doc.id,
              ...doc.data(),
              organization: doc.data().company,
              role: type === peopleType.EXHIBITOR ? peopleType.EXHIBITOR : peopleType.SPONSOR,
              type: type === peopleType.EXHIBITOR ? peopleType.EXHIBITOR : peopleType.SPONSOR
            })
          })
        }
        people.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
      }
    } else {
      const snapshot = await FirestoreDatabase.collection(Collection.ATTENDEES)
        .where('conferenceId', '==', conferenceId)
        .where('role', 'in', [peopleType.ATTENDEE, peopleType.SPEAKER, peopleType.MODERATOR])
        .get()
      snapshot.forEach((doc) => {
        people.push({
          id: doc.id,
          ...doc.data(),
          organization: doc.data().company,
          type: peopleType.ATTENDEE
        })
      })
      people.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
    }
    return people
  } catch (err) {
    console.error(err)
    toast.error(err?.message)
  }
}

export const getExistingCountersForType = async (conferenceId, type) => {
  try {
    let query = FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).where(
      'conferenceId',
      '==',
      conferenceId
    )
    if (type) query = query.where('type', '==', type)
    const snapshot = await query.get()
    const taken = new Set()
    snapshot.forEach((doc) => taken.add(doc.data().counter))
    return taken
  } catch (err) {
    console.error(err)
    return new Set()
  }
}

export const badgeCounterExists = async (counter, conferenceId, type) => {
  let query = FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
    .where('conferenceId', '==', conferenceId)
    .where('counter', '==', Number(counter))
  if (type) query = query.where('type', '==', type)
  const badgesSnapshot = await query.get()
  return badgesSnapshot?.docs?.length > 0
}

export const getHighestBadgeCounter = async (conferenceId, type) => {
  try {
    let query = FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).where(
      'conferenceId',
      '==',
      conferenceId
    )
    if (type) query = query.where('type', '==', type)
    const snapshot = await query.orderBy('counter', 'desc').limit(1).get()
    const doc = !snapshot.empty ? first(snapshot.docs) : null
    const highestBadge = doc?.exists ? { id: doc.id, ...doc.data() } : null
    return highestBadge
  } catch (err) {
    console.error(err)
  }
}

export const getBadgeSubject = (subjectId) => async (dispatch) => {
  try {
    const docSnap = await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
      .doc(subjectId)
      .get()
    const subject = { id: docSnap.id, ...docSnap.data() }
    dispatch(badgesSlice.actions.setBadgeSubject(subject))
  } catch (err) {
    console.error(err)
  }
}

export const addBadgeSubject = async (payload, conferenceId, onClose) => {
  try {
    const docRef = await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).add({
      ...payload,
      conferenceId,
      createdAt: firebase.firestore.FieldValue.serverTimestamp()
    })
    const doc = await docRef.get()
    const savedSubject = { id: doc.id, ...doc.data() }
    toast.success(`${payload.type} Subject added successfully`)
    onClose && onClose()
    return savedSubject
  } catch (err) {
    console.error(err)
    toast.error('Could not add badge subject.')
    return null
  }
}

export const updateBadgeSubject = async (payload, subjectId, conferenceId, onClose) => {
  try {
    await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
      .doc(subjectId)
      .update({ ...payload, conferenceId })
    const doc = await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).doc(subjectId).get()
    const savedSubject = { id: doc.id, ...doc.data() }
    toast.success(`${payload.type} Subject updated successfully`)
    onClose && onClose()
    return savedSubject
  } catch (err) {
    console.error(err)
    toast.error('Could not update badge subject.')
    return null
  }
}

export const deleteBadgeSubject = async (id) => {
  try {
    await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).doc(id).delete()
    toast.success('Badge subject deleted.')
  } catch (err) {
    console.error(err)
    toast.error('Could not delete subject(s). Try again.')
  }
}

export const bulkDeleteBadgeSubjects = async (ids) => {
  try {
    const idChunks = chunk(ids, 499)
    for (const chunkIds of idChunks) {
      const batch = FirestoreDatabase.batch()
      chunkIds.forEach((id) => {
        const docRef = FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).doc(id)
        batch.delete(docRef)
      })
      await batch.commit()
    }
    toast.success(`${ids.length} badge subject(s) deleted successfully.`)
  } catch (err) {
    console.error(err)
    toast.error('Could not delete subject(s). Try again.')
  }
}

export const bulkCreateBadgeSubjectsFromCSV = async (rows, conferenceId, subjectType) => {
  try {
    const highestBadge = await getHighestBadgeCounter(conferenceId, subjectType)
    let nextCounter = highestBadge ? highestBadge.counter + 1 : 1

    const rowChunks = chunk(rows, 499)
    for (const chunkRows of rowChunks) {
      const batch = FirestoreDatabase.batch()
      chunkRows.forEach((row) => {
        const docRef = FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).doc()
        const counter = row.counter ? parseInt(row.counter, 10) : nextCounter++
        batch.set(docRef, {
          counter,
          type: subjectType,
          firstName: row.firstName?.trim() || '',
          lastName: row.lastName?.trim() || '',
          email: row.email?.toLowerCase().trim() || '',
          credential: row.credential?.trim() || '',
          position: row.position?.trim() || '',
          organization: row.organization?.trim() || '',
          phone: row.phone?.trim() || '',
          city: row.city?.trim() || '',
          state: row.state?.trim() || '',
          country: row.country?.trim() || '',
          conferenceId,
          createdAt: firebase.firestore.FieldValue.serverTimestamp()
        })
      })
      await batch.commit()
    }

    toast.success(`${rows.length} badge subject(s) imported successfully.`)
  } catch (err) {
    console.error(err)
    toast.error('Import failed. Try again.')
    throw err
  }
}

export const getBadgeSubjectsData = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
    .where('conferenceId', '==', conferenceId)
    .orderBy('counter', 'asc')
    .onSnapshot((docs) => {
      const subjects = []
      docs.forEach((doc) => {
        subjects.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(subjects)
    })
}

export const getBadgeConfigurations = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
    .where('conferenceId', '==', conferenceId)
    .orderBy('createdAt', 'asc')
    .onSnapshot((documents) => {
      const configurations = []
      documents.forEach((doc) => {
        configurations.push({
          id: doc.id,
          ...doc.data()
        })
      })
      const newConfigs = configurations?.map((item, i) => ({ ...item, srNo: i + 1 }))
      callback(newConfigs)
    })
}

// recentLabels is stored as a map keyed by subject type (e.g. { Attendee: [...], Speaker: [...] })
// so configs shared across multiple subject types (Attendee | Speaker) keep independent label history.
export const updateRecentBadgeLabels = async (
  configurationId,
  subjectType,
  label,
  existingLabelsByType = {}
) => {
  if (!label?.trim() || !configurationId || !subjectType) return existingLabelsByType
  const existingLabels = Array.isArray(existingLabelsByType[subjectType])
    ? existingLabelsByType[subjectType]
    : []
  const updated = [label, ...existingLabels.filter((l) => l !== label)].slice(0, 5)
  const updatedByType = { ...existingLabelsByType, [subjectType]: updated }
  await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
    .doc(configurationId)
    .update({ recentLabels: updatedByType })
  return updatedByType
}

export const clearRecentBadgeLabels = async (
  configurationId,
  subjectType,
  existingLabelsByType = {}
) => {
  if (!configurationId || !subjectType) return existingLabelsByType
  const updatedByType = { ...existingLabelsByType, [subjectType]: [] }
  await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
    .doc(configurationId)
    .update({ recentLabels: updatedByType })
  return updatedByType
}

export const applicableBadgeConfiguration = async (type, conferenceId) => {
  try {
    const snapshots = await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const configurations = snapshots.docs.map((e) => ({ id: e.id, ...e.data() }))
    const applicableConfig = configurations?.find((e) => e?.applicableSubjects?.includes(type))
    return applicableConfig ?? null
  } catch (err) {
    console.error(err)
  }
}

const serializeBadgeLayout = (layout) => {
  if (!Array.isArray(layout)) return {}
  const result = {}
  layout.forEach((line, i) => {
    result[String(i)] = Array.isArray(line) ? line : []
  })
  return result
}

const deserializeBadgeLayout = (stored) => {
  const fallback = [['firstName', 'lastName'], [], [], [], []]
  if (!stored || typeof stored !== 'object') return fallback
  const layout = Array.from({ length: 5 }, (_, i) => stored[String(i)] || [])
  // Line 1 is always locked to firstName/lastName
  layout[0] = ['firstName', 'lastName']
  return layout
}

export const getBadgeConfiguration = (configurationId) => async (dispatch) => {
  try {
    const docSnap = await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .doc(configurationId)
      .get()
    const data = docSnap.data()
    const configuration = {
      id: docSnap.id,
      ...data,
      badgeLayout: deserializeBadgeLayout(data?.badgeLayout)
    }
    dispatch(badgesSlice.actions.setBadgeConfiguration(configuration))
  } catch (err) {
    console.error(err)
  }
}

export const updateBadgeConfiguration = async (
  configurationId,
  payload,
  conferenceId,
  onSuccess,
  onError
) => {
  try {
    const { applicableSubjects } = payload

    const results = await checkIfAppliedSubjectExistsInOtherConfiguration({
      isUpdating: true,
      configurationId: configurationId,
      applicableSubjects,
      conferenceId
    })

    if (results?.subjectFound) {
      toast.warning(
        `This configuration cannot be applied to the ${results?.subject} subject(s) because ${results?.subject} already exists in another configuration.`
      )
      onError && onError()
      return
    }

    await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .doc(configurationId)
      .update({
        ...payload,
        badgeLayout: serializeBadgeLayout(payload.badgeLayout),
        conferenceId: conferenceId
      })
    toast.success('Print configuration updated.')
    onSuccess && onSuccess()
  } catch (err) {
    console.error(err)
    toast.error('Could not update print configuration.')
    onError && onError()
  }
}

export const deleteBadgeConfiguration = async (configurationId) => {
  try {
    await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .doc(configurationId)
      .delete()
    toast.success('Print configuration deleted.')
  } catch (err) {
    console.error(err)
    toast.error('Could not delete print configuration.')
  }
}

export const addBadgeConfiguration = async (payload, conferenceId, onSuccess, onError) => {
  try {
    const { applicableSubjects } = payload

    const results = await checkIfAppliedSubjectExistsInOtherConfiguration({
      isUpdating: false,
      configurationId: '',
      applicableSubjects,
      conferenceId
    })

    if (results?.subjectFound) {
      toast.warning(
        `This configuration cannot be applied to the ${results?.subject} subject(s) because ${results?.subject} already exists in another configuration.`
      )
      onError && onError()
      return
    }

    await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS).add({
      ...payload,
      badgeLayout: serializeBadgeLayout(payload.badgeLayout),
      conferenceId: conferenceId
    })
    toast.success('Print configuration added.')
    onSuccess && onSuccess()
  } catch (err) {
    console.error(err)
    toast.error('Could not add print configuration.')
    onError && onError()
  }
}

export const checkIfAppliedSubjectExistsInOtherConfiguration = async ({
  isUpdating,
  configurationId,
  applicableSubjects,
  conferenceId
}) => {
  const configurationsSnapshot = isUpdating
    ? await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
        .where('conferenceId', '==', conferenceId)
        .where(firebase.firestore.FieldPath.documentId(), '!=', configurationId)
        .get()
    : await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
        .where('conferenceId', '==', conferenceId)
        .get()

  let foundSubjects = []
  if (!configurationsSnapshot.empty) {
    const configs = configurationsSnapshot?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
    for (let i = 0; i < configs.length; i++) {
      for (let j = 0; j < applicableSubjects.length; j++) {
        if (configs[i]['applicableSubjects'].includes(applicableSubjects[j])) {
          // subject found inside
          foundSubjects.push(applicableSubjects[j])
        }
      }
    }
  }
  return {
    subjectFound: !!foundSubjects?.length,
    subject: foundSubjects?.length ? foundSubjects?.join(', ') : ''
  }
}

export default badgesSlice
